/**
 * @callback comparator
 * @param {*} valueA
 * @param {*} valueB
 * @return {number}
 */

/**
 * Returns whether `numA` should come before or after `numB` when sorted by numerical value. If
 * either argument is not a `number`, it is converted to a `number` using the `Number` function. If
 * that conversion results in `NaN`, then the return value is `NaN`. When used as a comparator
 * passed to `Array.prototype.sort`, the value will be sorted to the end of the `Array`.
 *
 * @param {number|*} numA
 * @param {number|*} numB
 * @return {number}
 */
export const compareAsNumbers = (numA, numB) => Number(numA) - Number(numB);

/**
 * Returns whether `magA` should come before or after `magB` when sorted by absolute numerical
 * value. If either argument is not a `number`, it is converted to a `number` using the `Number`
 * function before taking its absolute value. If that conversion results in `NaN`, then the return
 * value is `NaN`. When used as a comparator passed to `Array.prototype.sort`, the value will be
 * sorted to the end of the `Array`.
 *
 * @param {number|*} magA
 * @param {number|*} magB
 * @return {number}
 */
export const compareAsMagnitudes = (magA, magB) => Math.abs(Number(magA)) - Math.abs(Number(magB));

/**
 * Returns whether `dateA` should come before or after `dateB` when sorted chronologically. If
 * either argument is not a `Date`, it is converted to a `Date` using the `new Date` constructor
 * function before taking its temporal value. If calling `new Date` with an argument results in an
 * "Invalid Date" (a `Date` with a timestamp value of `NaN`) then the return value is `NaN`. When
 * used as a comparator passed to `Array.prototype.sort`, the value will be sorted to the end of the
 * `Array`.
 *
 * @param {Date|string|number|*} dateA
 * @param {Date|string|number|*} dateB
 * @return {number}
 */
export const compareAsDates = (dateA, dateB) =>
  compareAsNumbers(new Date(dateA).getTime(), new Date(dateB).getTime());

/**
 * Returns whether `stringA` should come before or after `stringB` when sorted alphabetically, case-
 * insensitive. If either argument is not a `string`, it is converted to a `string` using the
 * `String` function.
 *
 * @param {string|*} stringA
 * @param {string|*} stringB
 * @return {number}
 */
export const compareAsStrings = (stringA, stringB) => {
  const normalizedA = String(stringA).toLowerCase();
  const normalizedB = String(stringB).toLowerCase();
  if (normalizedA > normalizedB) return 1;
  if (normalizedA < normalizedB) return -1;
  return 0;
};

/**
 * Creates a comparator function as needed by `Array.prototype.sort` by combining the provided
 * comparator functions in the order they are provided. If the result of passing two objects to
 * the first comparator is `0`, then compares them with the second comparator function, and so on.
 *
 * @param {Array<comparator>} comparators
 * @return {comparator}
 */
export const combineComparators = comparators => (valueA, valueB) =>
  comparators.reduce(
    (comparison, comparator) => (comparison === 0 ? comparator(valueA, valueB) : comparison),
    0,
  );
